import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Card, CardBody, CardFooter, Layer, Stack, Text } from "grommet";
import { Wheel, WheelMedia, useWheel } from "@cxde/wheel-of-fortune";
import "@cxde/wheel-of-fortune/style.css";
import { WheelSettings } from "../wheel-settings/WheelSettings";
import { WheelGrid } from "./WheelGrid";
import { useDispatch, useSelector } from "react-redux";
import { removeLot } from "../../slices/wheelDataSlice";
import { setAutoSpin, setWheelDuration, setWheelSoundVolume, setWheelTheme } from "../../slices/wheelSettingsSlice";
import wheelThemes, {
	BUILT_IN_WHEEL_THEME_IDS,
	createWheelThemeSnapshot,
	DEFAULT_WHEEL_THEME_ID,
	getWheelTheme,
	getWheelThemeSafariMediaUrl,
	isSafariBrowser,
} from "./wheelThemes";
import { WinnerCard } from "./cards/WinnerCard";
import { EliminatedCard } from "./cards/EliminatedCard";
import { WheelAppearanceEditor } from "../wheel-customization/WheelAppearanceEditor";
import { useWheelAppearance } from "../wheel-customization/useWheelAppearance";
import { defaultWheelAppearance } from "../wheel-customization/wheelAppearance";
import { loadFontStylesheets } from "../wheel-customization/fontMediaUtils";
import { useGetMediasByCategoryQuery } from "../../api/mediasApi";
import { useTranslation } from "react-i18next";
import { createWheelSoundConfig } from "./wheelSoundPresets";
import { AuctionWheelFrogs } from "./auction-wheel-frogs/AuctionWheelFrogs";
import { WinnerCelebration } from "./winner-celebration/WinnerCelebration";
import "./WheelViewTab.css";

const emptyWheelItem = {
	id: '__empty-wheel',
	label: '',
	weight: 1,
	color: '#01A982',
};

const generateRandomColor = () => {
	const colorPart = () => (Math.floor(Math.random() * 180) + 50).toString(16).padStart(2, '0');
	return `#${colorPart()}${colorPart()}${colorPart()}`;
};

const loadWheelColors = () => {
	try {
		const savedColors = JSON.parse(localStorage.getItem('wheel-colors'));
		return savedColors && typeof savedColors === 'object' ? { ...savedColors } : {};
	} catch {
		return {};
	}
};

const getSpinDuration = (duration) => {
	const parsedDuration = Number(duration);
	return Number.isFinite(parsedDuration) && parsedDuration > 0 ? parsedDuration : 10;
};

const getSpinEasing = () => 'cubic-bezier(0.12, 0.82, 0.18, 1)';

const handleSectorPass = () => undefined;

export const WheelViewTab = () => {
	const { t } = useTranslation('wheel', { useSuspense: false });
	const { wheelData, isNotLocked } = useSelector((state) => state.wheelData);
	const { autoSpin, wheelSpinMode, wheelDuration, randomSpinDuration, wheelDurationMin, wheelDurationMax, wheelTheme, wheelSize, emote, wheelSoundVolume, showChances, spinStarted } = useSelector((state) => state.wheelSettings);
	const { data: fontMedias } = useGetMediasByCategoryQuery({ categoryCode: 'roulette_category_font' });

	const dispatch = useDispatch();

	const wheelController = useWheel();
	const wheelRef = useRef({});
	const {
		appearance,
		mediaUrls,
		isLoaded: areAppearanceThemesLoaded,
		isDirty: isAppearanceDirty,
		activeThemeId,
		updateValue,
		updateSector,
		setMedia,
		setMediaUrl,
		resetAppearance,
		discardAppearanceChanges,
		selectTheme,
		appearanceThemes,
		saveAppearanceTheme,
		updateAppearanceTheme,
		importAppearanceTheme,
		renameAppearanceTheme,
		removeAppearanceTheme,
	} = useWheelAppearance({
		initialThemeId: wheelTheme,
		fallbackWheel: createWheelThemeSnapshot(getWheelTheme(wheelTheme)),
	});
	const wheelColorsRef = useRef();
	if (wheelColorsRef.current === undefined) {
		wheelColorsRef.current = loadWheelColors();
	}
	const [showWinningSector, setShowWinningSector] = useState(false);
	const [winningSector, setWinningSector] = useState();
	const [isElimination, setIsElimination] = useState(false);
	const [isSpinning, setIsSpinning] = useState();
	const [currentSpinDuration, setCurrentSpinDuration] = useState(() => getSpinDuration(wheelDuration));
	const [isAppearanceOpen, setIsAppearanceOpen] = useState(false);
	const [appearanceInitialSection, setAppearanceInitialSection] = useState('wheel');
	const [pendingThemeId, setPendingThemeId] = useState('');
	const customSoundRefs = useRef({
		start: null,
		win: null,
	});

	// A theme only persists the font family name. Load the public font catalogue
	// here, rather than only in the appearance editor, so saved themes render
	// with their selected font immediately after a page refresh.
	useEffect(() => {
		loadFontStylesheets(fontMedias);
	}, [fontMedias]);

	const themeOptions = useMemo(() => {
		const savedThemesById = new Map(
			appearanceThemes.map((theme) => [theme.id, theme]),
		);
		const builtInThemeIds = new Set(BUILT_IN_WHEEL_THEME_IDS);
		const builtInThemes = BUILT_IN_WHEEL_THEME_IDS.map((id) => {
			const theme = wheelThemes[id];

			return {
				id,
				type: 'builtin',
				nameKey: id === 'classic'
					? 'mainTheme'
					: id === 'christmas'
						? 'christmasTheme'
						: id === 'minimalistic'
							? 'noneTheme'
							: id === 'neon'
								? 'neonTheme'
								: 'realisticSpaceTheme',
				wheel: savedThemesById.get(id)?.wheel || createWheelThemeSnapshot(theme),
				appearance: savedThemesById.get(id)?.appearance || theme.appearance || defaultWheelAppearance,
				defaultAppearance: theme.appearance || defaultWheelAppearance,
			};
		});
		const customThemes = appearanceThemes
			.filter((theme) => !builtInThemeIds.has(theme.id))
			.map((theme) => ({
			id: theme.id,
			type: 'custom',
			name: theme.name,
			wheel: theme.wheel,
			appearance: theme.appearance,
			defaultAppearance: theme.appearance,
		}));

		return [...builtInThemes, ...customThemes];
	}, [appearanceThemes]);

	const selectedThemeOption = themeOptions.find((theme) => theme.id === wheelTheme)
		|| themeOptions.find((theme) => theme.id === DEFAULT_WHEEL_THEME_ID);

	const switchToTheme = useCallback((theme) => {
		if (!theme) {
			return;
		}

		selectTheme(theme.id, theme.appearance, theme.defaultAppearance);
		dispatch(setWheelTheme(theme.id));
		setPendingThemeId('');
	}, [dispatch, selectTheme]);

	useEffect(() => {
		if (!areAppearanceThemesLoaded) {
			return;
		}

		if (selectedThemeOption) {
			if (activeThemeId !== selectedThemeOption.id) {
				switchToTheme(selectedThemeOption);
			}
			return;
		}

		switchToTheme(themeOptions.find((theme) => theme.id === DEFAULT_WHEEL_THEME_ID));
	}, [activeThemeId, areAppearanceThemesLoaded, selectedThemeOption, switchToTheme, themeOptions]);

	const handleThemeSelect = useCallback((themeId) => {
		if (themeId === activeThemeId) {
			return;
		}

		const nextTheme = themeOptions.find((theme) => theme.id === themeId);
		if (!nextTheme) {
			return;
		}

		if (isAppearanceDirty) {
			setPendingThemeId(themeId);
			return;
		}

		switchToTheme(nextTheme);
	}, [activeThemeId, isAppearanceDirty, switchToTheme, themeOptions]);

	const handleSaveAsTheme = useCallback(async (name) => {
		const savedTheme = await saveAppearanceTheme(
			name,
			createWheelThemeSnapshot(selectedThemeOption?.wheel),
		);
		if (savedTheme) {
			switchToTheme({
				id: savedTheme.id,
				type: 'custom',
				name: savedTheme.name,
				wheel: savedTheme.wheel,
				appearance: savedTheme.appearance,
			});
		}
		return savedTheme;
	}, [saveAppearanceTheme, selectedThemeOption, switchToTheme]);

	const handleUpdateTheme = useCallback(async () => {
		if (!selectedThemeOption) {
			return null;
		}

		return updateAppearanceTheme(
			selectedThemeOption.id,
			createWheelThemeSnapshot(selectedThemeOption.wheel),
			selectedThemeOption.name || t(selectedThemeOption.nameKey),
		);
	}, [selectedThemeOption, t, updateAppearanceTheme]);

	const handleImportTheme = useCallback(async (importedTheme, mode) => {
		if (mode === 'replace') {
			if (!selectedThemeOption) {
				return null;
			}

			return updateAppearanceTheme(
				selectedThemeOption.id,
				createWheelThemeSnapshot(importedTheme.wheel),
				selectedThemeOption.name || t(selectedThemeOption.nameKey),
				importedTheme.appearance,
			);
		}

		const savedTheme = await importAppearanceTheme(importedTheme);
		if (!savedTheme) {
			return null;
		}

		const themeOption = {
			id: savedTheme.id,
			type: 'custom',
			name: savedTheme.name,
			wheel: savedTheme.wheel,
			appearance: savedTheme.appearance,
		};
		if (isAppearanceDirty) {
			setPendingThemeId(savedTheme.id);
		} else {
			switchToTheme(themeOption);
		}
		return savedTheme;
	}, [
		importAppearanceTheme,
		isAppearanceDirty,
		selectedThemeOption,
		switchToTheme,
		t,
		updateAppearanceTheme,
	]);

	const handleRenameTheme = useCallback((name) => {
		if (selectedThemeOption?.type !== 'custom') {
			return Promise.resolve(null);
		}
		return renameAppearanceTheme(selectedThemeOption.id, name);
	}, [renameAppearanceTheme, selectedThemeOption]);

	const handleRemoveTheme = useCallback(async () => {
		if (selectedThemeOption?.type !== 'custom') {
			return false;
		}

		const wasRemoved = await removeAppearanceTheme(selectedThemeOption.id);
		if (wasRemoved) {
			setIsAppearanceOpen(false);
			switchToTheme(themeOptions.find((theme) => theme.id === DEFAULT_WHEEL_THEME_ID));
		}
		return wasRemoved;
	}, [removeAppearanceTheme, selectedThemeOption, switchToTheme, themeOptions]);

	const pendingTheme = themeOptions.find((theme) => theme.id === pendingThemeId);
	const pendingThemeName = pendingTheme?.name || (pendingTheme?.nameKey ? t(pendingTheme.nameKey) : '');
	const handleSaveAndSwitch = useCallback(async () => {
		const savedTheme = await handleUpdateTheme();
		if (!savedTheme) {
			return;
		}

		switchToTheme(pendingTheme);
	}, [handleUpdateTheme, pendingTheme, switchToTheme]);

	const handleDiscardAndSwitch = useCallback(async () => {
		await discardAppearanceChanges();
		switchToTheme(pendingTheme);
	}, [discardAppearanceChanges, pendingTheme, switchToTheme]);

	const stopCustomSound = useCallback((slot, reset = true) => {
		const audio = customSoundRefs.current[slot]?.audio;
		if (!audio) {
			return;
		}

		audio.pause();
		audio.loop = false;
		if (reset) {
			try {
				audio.currentTime = 0;
			} catch {
				// Ignore browsers that block manual seek before metadata is loaded.
			}
		}
	}, []);

	const getCustomSound = useCallback((slot) => {
		const src = mediaUrls.sounds?.[slot];
		if (!src || typeof Audio === 'undefined') {
			return null;
		}

		const current = customSoundRefs.current[slot];
		if (current?.src === src) {
			return current.audio;
		}

		stopCustomSound(slot);

		const audio = new Audio(src);
		audio.preload = 'auto';
		customSoundRefs.current[slot] = { src, audio };
		return audio;
	}, [mediaUrls.sounds, stopCustomSound]);

	const playCustomSound = useCallback((slot, { loop = false } = {}) => {
		const audio = getCustomSound(slot);
		if (!audio) {
			return false;
		}

		audio.pause();
		audio.loop = loop;
		audio.volume = Number.isFinite(Number(wheelSoundVolume)) ? Number(wheelSoundVolume) : 0.2;
		try {
			audio.currentTime = 0;
		} catch {
			// Ignore browsers that block manual seek before metadata is loaded.
		}
		audio.play().catch(() => undefined);
		return true;
	}, [getCustomSound, wheelSoundVolume]);

	const handleIsSpinning = useCallback((isSpinning) => {
		setIsSpinning(isSpinning);
		if (isSpinning) {
			setShowWinningSector(false);
			playCustomSound('start');
		}
	}, [playCustomSound]);

	const showFinalWinner = (finalWinner) => {
		setShowWinningSector(false);
		setTimeout(() => {
			setWinningSector(finalWinner);
			setIsElimination(false);
			setShowWinningSector(true);
			playCustomSound('win');
			if (autoSpin) {
				dispatch(setAutoSpin(false));
			}
		}, 300);
	};

	const handleWheelWinningSector = (winningSector) => {
		setWinningSector(winningSector);
		setShowWinningSector(true);

		const isElim = (wheelSpinMode === 2 || wheelSpinMode === 3) && wheelData.length > 1;
		setIsElimination(isElim);

		if (isElim) {
			dispatch(removeLot(winningSector));

			if (wheelData.length === 2) {
				const finalWinner = wheelData.find(item => item.id !== winningSector.id);
				setTimeout(() => showFinalWinner(finalWinner), 1500);
			} else if (autoSpin) {
				setTimeout(() => {
					setShowWinningSector(false);
					let currentDuration = parseInt(wheelDuration);
					if (randomSpinDuration) {
						const min = parseInt(wheelDurationMin) || 5;
						const max = parseInt(wheelDurationMax) || 15;
						currentDuration = Math.floor(Math.random() * (max - min + 1) + min);
						dispatch(setWheelDuration(currentDuration));
					}
					if (wheelRef.current) {
						wheelRef.current.spinWheel(currentDuration);
					}
				}, 1500);
			}
		} else {
			playCustomSound('win');
			if (autoSpin) {
				dispatch(setAutoSpin(false));
			}
		}
	};

	const wheelItems = useMemo(() => {
		if (wheelData.length === 0) {
			return [emptyWheelItem];
		}

		return wheelData.map((item, index) => {
			const id = String(item.id);
			const sectorAppearance = appearance.sectorOverrides[id] || {};
			const generatedColor = item.backgroundColor || wheelColorsRef.current[id] || generateRandomColor();
			const paletteColor = appearance.sectors.palette[index % appearance.sectors.palette.length];
			const color = sectorAppearance.color || (
				appearance.sectors.colorMode === 'palette' ? paletteColor : generatedColor
			);
			wheelColorsRef.current[id] = color;

			const chance = Number(item.chance);
			const imageSource = mediaUrls.sectors[id] || item.image;
			const image = imageSource ? {
				src: imageSource,
				opacity: sectorAppearance.imageOpacity ?? (
					Number.isFinite(Number(item.imageOpacity)) ? Number(item.imageOpacity) : 1
				),
				fit: sectorAppearance.imageFit || 'cover',
				rotation: sectorAppearance.imageRotation ?? (
					Number.isFinite(Number(item.imageRotation)) ? Number(item.imageRotation) : 0
				),
				scale: sectorAppearance.imageScale ?? (
					Number.isFinite(Number(item.imageScale)) ? Number(item.imageScale) : 1
				),
				offsetX: sectorAppearance.imageOffsetX ?? 0,
				offsetY: sectorAppearance.imageOffsetY ?? 0,
			} : undefined;

			return {
				id,
				label: chance >= 0.01 ? String(item.lotName ?? '') : '',
				weight: Number.isFinite(chance) && chance > 0 ? chance : 0,
				color,
				image,
				text: {
					color: sectorAppearance.textColor || item.labelColor || appearance.text.color,
				},
				data: item,
			};
		});
	}, [appearance.sectorOverrides, appearance.sectors, appearance.text.color, mediaUrls.sectors, wheelData]);

	useEffect(() => {
		localStorage.setItem('wheel-colors', JSON.stringify(wheelColorsRef.current));
	}, [wheelItems]);

	const spinWheel = useCallback((overrideDuration) => {
		if (wheelData.length === 0 || wheelController.getState().status !== 'idle') {
			return;
		}

		const actualDuration = getSpinDuration(overrideDuration ?? wheelDuration);
		const minimumRotations = Math.max(1, Math.ceil(actualDuration));
		setCurrentSpinDuration(actualDuration);

		wheelController.spin({
			mode: 'client',
			landing: {
				mode: 'random',
			},
			animation: {
				duration: actualDuration * 1000,
				rotations: {
					min: minimumRotations,
					max: minimumRotations * 2,
				},
				easing: getSpinEasing(),
			},
		}).catch(() => {
			handleIsSpinning(false);
		});
	}, [handleIsSpinning, wheelController, wheelData.length, wheelDuration]);

	wheelRef.current.spinWheel = spinWheel;

	useEffect(() => {
		Object.values(customSoundRefs.current).forEach((entry) => {
			if (entry?.audio) {
				entry.audio.volume = wheelSoundVolume;
			}
		});
	}, [wheelSoundVolume]);

	useEffect(() => {
		Object.entries(customSoundRefs.current).forEach(([slot, entry]) => {
			if (entry && entry.src !== mediaUrls.sounds?.[slot]) {
				entry.audio.pause();
				customSoundRefs.current[slot] = null;
			}
		});
	}, [mediaUrls.sounds]);

	useEffect(() => () => {
		Object.values(customSoundRefs.current).forEach((entry) => {
			if (entry?.audio) {
				entry.audio.pause();
			}
		});
	}, []);

	useEffect(() => {
		if (!spinStarted || isNotLocked) {
			setShowWinningSector(false);
		}
	}, [spinStarted, isNotLocked]);

	const selectedWheelTheme = selectedThemeOption?.wheel || getWheelTheme(DEFAULT_WHEEL_THEME_ID);
	const wheelRadius = appearance.wheel.radius === null
		? selectedWheelTheme.radius
		: appearance.wheel.radius / 100;
	const rotorInset = `${Math.max(0, (1 - wheelRadius) * 50)}%`;
	const customizedTheme = {
		...selectedWheelTheme.theme,
		background: appearance.wheel.background,
		sector: {
			colors: appearance.sectors.palette,
		},
		text: appearance.text,
		border: appearance.border,
		dividers: appearance.dividers,
	};
	const getMediaKind = (asset, src) => {
		if (asset?.kind === 'video' || asset?.type?.startsWith('video/')) {
			return 'video';
		}
		if (typeof src === 'string' && /\.(mp4|m4v|mov|ogv|webm)(?:[?#].*)?$/i.test(src)) {
			return 'video';
		}
		return 'image';
	};
	const mediaStyle = (slot) => ({
		width: '100%',
		height: '100%',
		objectFit: 'contain',
		opacity: slot.opacity,
		transform: `scale(${slot.scale})`,
	});
	const renderMedia = (src, asset, slot, alt) => {
		if (!src) {
			return undefined;
		}

		const mediaKind = getMediaKind(asset, src);
		const safariSrc = mediaKind === 'video' && isSafariBrowser()
			? getWheelThemeSafariMediaUrl(src, asset)
			: null;
		const mediaKey = `${mediaKind}:${safariSrc || src}`;
		const videoProps = {
			autoPlay: true,
			loop: true,
			muted: true,
			playsInline: true,
			style: mediaStyle(slot),
		};

		if (safariSrc && safariSrc !== src) {
			return (
				<video key={mediaKey} {...videoProps} aria-hidden={alt ? undefined : true}>
					<source src={safariSrc} type="video/quicktime" />
					<source src={src} type="video/webm" />
				</video>
			);
		}

		return (
			<WheelMedia
				key={mediaKey}
				src={src}
				as={mediaKind}
				alt={alt}
				imageProps={{ style: mediaStyle(slot) }}
				videoProps={videoProps}
			/>
		);
	};
	const isNeonTheme = selectedThemeOption?.id === 'neon';
	const overlaySource = appearance.overlay.source === 'theme'
		? mediaUrls.overlay || selectedWheelTheme.overlayImage
		: mediaUrls.overlay;
	const overlay = appearance.overlay.source === 'none'
		? undefined
		: renderMedia(overlaySource, appearance.media.overlay, appearance.overlay, '');
	const centerAsset = appearance.center.source === 'theme' || appearance.center.source === 'custom'
		? appearance.media.center
		: null;
	const centerMedia = appearance.center.source === 'none' || appearance.center.source === 'emote'
		? undefined
		: renderMedia(mediaUrls.center, centerAsset, appearance.center, '');
	const centerEmote = emote && (appearance.center.source === 'emote' || appearance.center.showEmote)
		? renderMedia(emote, null, appearance.center, '')
		: undefined;
	const center = centerMedia || centerEmote ? (
		<span
			className={`auction-wheel__center-media${isNeonTheme ? ' auction-wheel__center-media--neon' : ''}`}
			style={{
				background: appearance.center.background,
				border: `${appearance.center.borderWidth}px solid ${appearance.center.borderColor}`,
				borderRadius: `${appearance.center.borderRadius}%`,
			}}
		>
			{centerMedia}
			{centerEmote && <span className="auction-wheel__center-emote">{centerEmote}</span>}
		</span>
	) : undefined;
	const pointerIsHidden = appearance.pointer.source === 'none'
		|| (appearance.pointer.source === 'custom' && !mediaUrls.pointer);
	const pointer = appearance.pointer.source === 'custom'
		? renderMedia(mediaUrls.pointer, appearance.media.pointer, appearance.pointer, '')
		: pointerIsHidden ? <span /> : undefined;
	const wheelClassName = `auction-wheel${pointerIsHidden ? ' auction-wheel--pointer-hidden' : ''}`;
	const normalizedWheelSoundVolume = Number.isFinite(Number(wheelSoundVolume))
		? Number(wheelSoundVolume)
		: 0.2;
	const wheelSoundConfig = createWheelSoundConfig({
		tickSound: appearance.sounds.tick,
		customSoundUrl: mediaUrls.sounds.tick,
		volume: normalizedWheelSoundVolume,
	});
	const openAppearance = (section = 'wheel') => {
		setAppearanceInitialSection(section);
		setIsAppearanceOpen(true);
	};

	return (
		<>
			<AuctionWheelFrogs
				isSpinning={Boolean(isSpinning)}
				spinDuration={currentSpinDuration}
				showElimination={showWinningSector && isElimination}
				showWinner={showWinningSector && !isElimination}
			/>
			<WinnerCelebration active={showWinningSector && !isElimination} />
			{pendingTheme && (
				<Layer
					modal
					position="center"
					onEsc={() => setPendingThemeId('')}
					onClickOutside={() => setPendingThemeId('')}
				>
					<Card width="medium" background="#222222">
						<CardBody pad="medium" gap="xsmall">
							<Text weight="bold">{t('appearanceUnsavedTitle')}</Text>
							<Text size="small" color="text-weak">
								{t('appearanceSwitchTarget', { name: pendingThemeName })}
							</Text>
							<Text size="small" color="text-weak">{t('appearanceUnsavedText')}</Text>
						</CardBody>
						<CardFooter justify="end" gap="small" pad="medium">
							<Button label={t('appearanceDiscardAndSwitch')} onClick={handleDiscardAndSwitch} />
							<Button primary label={t('appearanceSaveAndSwitch')} onClick={handleSaveAndSwitch} />
						</CardFooter>
					</Card>
				</Layer>
			)}
			<WheelGrid isAppearanceOpen={isAppearanceOpen}>
				<Box className="auction-wheel-workspace">
					<Stack anchor="center" fill>
						<Wheel
							controller={wheelController}
							items={wheelItems}
							renderer="auto"
							size="100%"
							maxSize={Number(wheelSize) || 768}
							className={wheelClassName}
							style={{
								'--auction-wheel-rotor-inset': rotorInset,
								'--auction-wheel-pointer-size': `${appearance.pointer.size}%`,
								'--auction-wheel-pointer-offset': `${appearance.pointer.offset}%`,
								'--auction-wheel-pointer-color': appearance.pointer.color,
								'--auction-wheel-pointer-opacity': appearance.pointer.opacity,
								'--auction-wheel-pointer-scale': appearance.pointer.scale,
							}}
							theme={customizedTheme}
							debug={appearance.wheel.debug}
							pointerPosition={appearance.pointer.position}
							pointer={pointer}
							overlay={overlay}
							center={center}
							centerSize={`${appearance.center.size}%`}
							sounds={wheelSoundConfig}
							idleAnimation={{
								...appearance.idleAnimation,
								easing: 'ease-in-out',
							}}
							minLabelAngle={3.6}
							showProbability={showChances}
							itemsTransition={{ mode: 'collapse', duration: 360 }}
							onSpinStart={() => handleIsSpinning(true)}
							onSectorPass={handleSectorPass}
							onSpinEnd={({ winner }) => {
								handleIsSpinning(false);
								handleWheelWinningSector(winner.data);
							}}
							onSpinCancel={() => handleIsSpinning(false)}
						/>
						<WinnerCard winningSector={winningSector} show={showWinningSector && !isElimination} onClose={() => setShowWinningSector(false)} />
						<EliminatedCard droppedOutSector={winningSector} show={showWinningSector && isElimination} onClose={() => setShowWinningSector(false)} />
					</Stack>
				</Box>
				<Box className="auction-wheel-settings" fill>
					{isAppearanceOpen ? (
						<WheelAppearanceEditor
							isOpen={isAppearanceOpen}
							onToggle={() => setIsAppearanceOpen((current) => !current)}
							showToggleButton={false}
							appearance={appearance}
							wheelData={wheelData}
							themeRadius={selectedWheelTheme.radius}
							updateValue={updateValue}
							updateSector={updateSector}
							setMedia={setMedia}
							setMediaUrl={setMediaUrl}
							resetAppearance={resetAppearance}
							theme={{
								...selectedThemeOption,
								name: selectedThemeOption?.name || t(selectedThemeOption?.nameKey),
							}}
							isDirty={isAppearanceDirty}
							onDiscardChanges={discardAppearanceChanges}
							onSaveAsTheme={handleSaveAsTheme}
							onUpdateTheme={handleUpdateTheme}
							onImportTheme={handleImportTheme}
							onRenameTheme={handleRenameTheme}
							onRemoveTheme={handleRemoveTheme}
							initialSection={appearanceInitialSection}
							customTickSoundUrl={mediaUrls.sounds.tick}
							wheelSoundVolume={normalizedWheelSoundVolume}
							onWheelSoundVolumeChange={(value) => dispatch(setWheelSoundVolume(value))}
						/>
					) : (
						<WheelSettings
							wheelRef={wheelRef}
							isSpinning={isSpinning}
							onToggleAppearance={openAppearance}
							themeOptions={themeOptions}
							selectedThemeId={selectedThemeOption?.id}
							onThemeSelect={handleThemeSelect}
							selectedTickSound={appearance.sounds.tick}
						/>
					)}
				</Box>
			</WheelGrid>
		</>
	);
};
